
import { Vue, Component } from 'vue-property-decorator';
import { Action, Getter } from 'vuex-class';
import { en, fr } from 'vuejs-datepicker/dist/locale';
import { DateTime } from 'luxon';
import Datepicker from 'vuejs-datepicker';
import ScheduledOrderCard from '@/components/profile/ScheduledOrderCard.vue';

const namespace: string = 'auth';

@Component<ScheduledOrders>({
	components: {
		Datepicker,
		ScheduledOrderCard
	}
})
export default class ScheduledOrders extends Vue {
	@Action('fetchScheduledOrders', { namespace }) private fetchScheduledOrders!: (payload: ScheduledOrdersPayload) => void;
	@Action('setSelectedScheduledOrder', { namespace }) private setSelectedScheduledOrder!: (order: Order) => void;
	@Getter('getScheduledOrders', { namespace }) private orders!: Order[];
	@Getter('showLoginAsBanner', { namespace }) private showLoginAsBanner!: boolean;
	@Getter('getUser', { namespace }) private user!: UserInfo;
	@Getter('getRestaurant', { namespace: 'restaurant' }) private restaurant!: Restaurant;

	private showScrollToTopButton: boolean = false;
	private fetchingOrders: boolean = false;
	private datepickerLocales: object = {
		en,
		fr
	};

	/**
	 * Get the dates with orders to highlight on the calendar
	 *
	 * @return {Date[]}
	 */
	private get highlightedDates(): Date[] {
		return this.orders.map((order: Order) => {
			if (order.type === 'catering') {
				return DateTime.fromISO(order.pickup_time || order.data.suites!.eventStartDate).toJSDate();
			}
			return DateTime.fromISO(order.pickup_time).toJSDate();
		});
	}

	/**
	 * Re-sort orders if any are missing a pickup_time (suites orders w/ event-based intervals)
	 * These orders may not be in the correct order since orders are sorted by pickup_time which is null
	 *
	 * @return {Order[]}
	 */
	private get sortedOrders(): Order[] {
		if (this.orders.some(order => !order.pickup_time)) {
			return this.orders.sort((a: Order, b: Order) => {
				const dateA = DateTime.fromISO(a.pickup_time || a.data.suites!.eventStartDate).toMillis();
				const dateB = DateTime.fromISO(b.pickup_time || b.data.suites!.eventStartDate).toMillis();
				return dateA - dateB;
			});
		}
		return this.orders;
	}

	/**
	 * Get the month that the calendar should start on (month of first order or current month if no orders)
	 *
	 * @return {Date}
	 */
	private get openDate(): Date {
		if (this.sortedOrders.length) {
			if (this.sortedOrders[0].type === 'catering') {
				return DateTime.fromISO(this.sortedOrders[0].pickup_time || this.sortedOrders[0].data.suites!.eventStartDate).toJSDate();
			}
			return DateTime.fromISO(this.sortedOrders[0].pickup_time).toJSDate();
		}
		return new Date();
	}

	/**
	 * Fetch scheduled orders for the user
	 *
	 * @return {Promise<void>}
	 */
	private async created(): Promise<void> {
		try {
			this.fetchingOrders = true;
			await this.fetchScheduledOrders({
				app8RestaurantId: this.restaurant.app8_restaurant,
				userId: this.user.id,
				token: this.user.token
			});
		}
		catch (error) {
			this.$toasted.show(this.$t('profile.scheduled_orders.error_fetching_orders'), { type: 'error', position: 'top-center' }).goAway(5000);
		}
		finally {
			this.fetchingOrders = false;
		}

		window.addEventListener('scroll', this.onScroll);
	}

	private beforeDestroy(): void {
		window.removeEventListener('scroll', this.onScroll);
	}

	/**
	 * Redirect to the profile page
	 *
	 * @return {void}
	 */
	private toProfile(): void {
		this.$router.push({ path: '/profile', query: this.$route.query }).catch(() => {});
	}

	/**
	 * Redirect to the order details page
	 *
	 * @param {Order} order
	 * @return {void}
	 */
	private toOrderDetails(order: Order): void {
		this.setSelectedScheduledOrder(order);
		this.$router.push({ path: `/profile/scheduled-orders/${order.id}`, query: this.$route.query }).catch(() => {});
	}

	/**
	 * Scroll to the selected date when clicking a date on the calendar
	 * Highlight all orders for that date
	 *
	 * @param {Date} date
	 * @return {void}
	 */
	private scrollToDate(date: Date): void {
		const dateString = DateTime.fromJSDate(date).toFormat('yyyy-MM-dd');
		const ordersForDate = this.orders.filter((order: Order) => {
			return DateTime.fromISO(order.pickup_time).toFormat('yyyy-MM-dd') === dateString;
		});
		if (ordersForDate.length) {
			scrollTo({
				top: document.getElementById(`order-${ordersForDate[0].id}`)!.offsetTop - 140,
				behavior: 'smooth'
			});

			// Highlight the orders for the selected date
			for (const order of ordersForDate) {
				const orderElement = document.getElementById(`order-${order.id}`);
				if (orderElement) {
					// Wait for the scroll to finish before highlighting
					setTimeout(() => {
						orderElement.classList.add('highlighted');

						setTimeout(() => {
							orderElement.classList.remove('highlighted');
						}, 1500);
					}, 250);
				}
			}
		}
	}

	/**
	 * Scroll to the top of the page
	 *
	 * @return {void}
	 */
	private scrollToTop(): void {
		scrollTo({
			top: 0,
			behavior: 'smooth'
		});
	}

	/**
	 * On scroll event, check if the user has scrolled more than 400px, if so, show the scroll to top button
	 *
	 * @param {any} e
	 * @return {void}
	 */
	private onScroll(e: any): void {
		const max: number = 400;
		const top: number = window.pageYOffset || e.target.scrollTop || 0;
		this.showScrollToTopButton = top > max;
	}
}
